import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { LoginService } from '../../login/login.service';
import { Router } from '@angular/router';
import { DataProtectionRequestsService } from '../_services/data-protection-requests.service';
import { FilterFieldConfig } from 'src/app/core/filter-panel/filter-field.model';
import { DataProtectionRequest, DataProtectionRequestStatus } from '../_types/data-protection-request';

@Component({
    selector: 'app-data-protection-list',
    templateUrl: './data-protection-list.component.html',
    styleUrls: ['./data-protection-list.component.scss'],
    host: { class: 'list-page' },
    standalone: false
})
export class DataProtectionListComponent implements OnInit {
  public dataProtectionRequests: DataProtectionRequest[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  public showCreateModal: boolean = false;
  public allowWrite: boolean = false;
  public allowDelete: boolean = true;
  public filters = {
    reason: new Filter(FilterOperation.contains, 'reason'),
    clientName: new Filter(FilterOperation.contains, 'clientName'),
    clientPhone: new Filter(FilterOperation.contains, 'clientPhone'),
    clientEmail: new Filter(FilterOperation.contains, 'clientEmail')
  };

  public fieldsConfig: FilterFieldConfig[] = [
    { filter: this.filters.reason, type: 'text', label: 'Razón', base: true },
    { filter: this.filters.clientName, type: 'text', label: 'Nombre cliente', base: true },
    { filter: this.filters.clientPhone, type: 'text', label: 'Teléfono', base: true },
    { filter: this.filters.clientEmail, type: 'text', label: 'Email', base: true }
  ];

  private lastGridEvent: any;

  constructor(
    private dataProtectionRequestsService: DataProtectionRequestsService,
    private topbarService: TopbarService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private loginService: LoginService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    if (!this.loginService.hasPermission('PROTECTION_DATA.ACCESS')) {
      this.router.navigate(['/home']);
      return;
    }

    this.allowWrite = this.loginService.hasPermission('PROTECTION_DATA.WRITE');
    this.topbarService.setTitle('Solicitudes de protección de datos');

    if (this.allowWrite) {
      this.topbarService.addAction('Nueva', 'pi pi-plus', false, () => {
        this.openCreateModal();
      });
    }
  }

  public openCreateModal(): void {
    if (!this.allowWrite) {
      return;
    }

    this.showCreateModal = true;
  }

  public closeCreateModal(): void {
    this.showCreateModal = false;
  }

  public async loadData($event: any): Promise<void> {
    this.lastGridEvent = $event;
    this.loading = true;

    try {
      const params: IParamsCollection = {
        include: ['company', 'createdByUser'],
        page: { number: $event.first + 1, size: $event.rows },
        sort: [($event.sortField ? ($event.sortOrder > 0 ? '' : '-') + $event.sortField : '-createdAt')],
        filter: FilterBuilder.fromObject(this.filters).build()
      };

      const response = await firstValueFrom(this.dataProtectionRequestsService.all(params));
      this.dataProtectionRequests = response.data;
      this.totalRecords = response.meta['total'];
      this.topbarService.setCount(this.totalRecords);
    } finally {
      this.loading = false;
    }
  }

  public clearFilters(): void {
    this.filters.reason.value = null;
    this.filters.clientName.value = null;
    this.filters.clientPhone.value = null;
    this.filters.clientEmail.value = null;
    this.search();
  }

  public search(): void {
    if (!this.lastGridEvent) {
      this.loadData({ first: 0, rows: 25, sortField: 'createdAt', sortOrder: -1 });
      return;
    }

    this.lastGridEvent.first = 0;
    this.loadData(this.lastGridEvent);
  }

  public getStatusLabel(status: DataProtectionRequestStatus): string {
    switch (status) {
      case 'success':
        return 'Correcta';
      case 'error':
        return 'Error';
      default:
        return 'Pendiente';
    }
  }

  public getStatusSeverity(status: DataProtectionRequestStatus): 'success' | 'danger' | 'warn' {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      default:
        return 'warn';
    }
  }

  public async downloadPdf(dataProtectionRequest: DataProtectionRequest): Promise<void> {
    try {
      const file = await this.dataProtectionRequestsService.exportPdf(dataProtectionRequest.id!);
      const url = window.URL.createObjectURL(file);
      window.open(url, '_blank');
    } catch (error: any) {
      this.messageService.add({ closable: true, severity: 'error', detail: error?.error?.title || 'Ha ocurrido un error al generar el PDF' });
    }
  }

  public delete(dataProtectionRequest: DataProtectionRequest): void {
    if (!this.allowDelete) {
      return;
    }

    this.confirmationService.confirm({
      message: '¿Está seguro que desea eliminar la solicitud?',
      header: 'Eliminar solicitud',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.dataProtectionRequestsService.delete(dataProtectionRequest.id).subscribe({
          next: () => {
            this.messageService.add({ closable: true, severity: 'success', detail: 'Solicitud eliminada correctamente' });
            this.search();
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error?.error?.title || 'Ha ocurrido un error al eliminar la solicitud' });
          }
        });
      }
    });
  }
}